import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  GET_TRENDS_SUPER_USER_DATA,
  GET_TRENDS_PENDING_DATA,
  GET_TRENDS_SALES_CHANNELS,
  GET_TRENDS_TOP_SALES,
  GET_TRENDS_TICKET_TIMELINE,
  LeagueSalesData,
  MarketplaceData,
  TrendsChannelTicket,
  TrendsChartDataPoint,
  TrendsTimelineTicket,
} from '../api/TrendsPageQuery';
import { GET_MANAGED_USERS_BY_SALES_REP, GET_SALES_REPS, GET_SALES_REPS_BY_EMAIL } from '../../Salesrep/api/SalesrepQuery';
import { GET_USERS_LIST } from '../../Users/api/UsersQuery';
import { GET_REVENUE_ADMIN, GET_TICKET_STATUS_COUNTS } from '../../Dashboard/api/DashboardQuery';
import { getDashboardAlignedRevenueVars } from '../../Dashboard/utils/revenueQueryVars';

export { getDashboardAlignedRevenueVars } from '../../Dashboard/utils/revenueQueryVars';

interface UseTrendsDataProps {
  startDate: string;
  endDate: string;
  period?: string;
  userId: string;
  userRole: string;
  userEmail?: string;
  /**
   * When true, timeline query does not filter by event date; results are narrowed in the client
   * to rows whose `latest_update` falls in [startDate, endDate] (end inclusive).
   * Use on Analytics where the timeframe should follow activity, not event schedule.
   */
  timelineActivityDateFilter?: boolean;
}

interface TrendsRevenuePoint {
  monthsName: string;
  revenue: number;
  totalTickets: number;
}

interface TrendsRevenueAggregate {
  overAllTickets: number;
  overAllSales: number;
  quickasystFee: number;
  avgListPrice: number;
}

interface TrendsTopSalesPoint {
  leagueName: string;
  totalCounts: number;
}

const LEAGUE_COLORS: Record<string, string> = {
  NBA: '#f97316',
  NFL: '#3b82f6',
  MLB: '#22c55e',
  NHL: '#111111',
  MLS: '#ec4899',
  'NCAA FB': '#6366f1',
  'NCAA BB': '#f59e0b',
  Concerts: '#14b8a6',
  Theater: '#8b5cf6',
};

const CHANNEL_COLORS: Record<string, string> = {
  StubHub: '#f97316',
  SeatGeek: '#ff5a1f',
  Ticketmaster: '#22c55e',
  'Vivid Seats': '#a855f7',
  TickPick: '#ec4899',
  Gametime: '#6366f1',
};

const normalizeToken = (value?: string | null) => {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
};

const normalizeLeagueName = (value?: string | null) => {
  const raw = (value ?? '').trim();
  const token = normalizeToken(raw);

  if (!token) {
    return 'Unknown';
  }

  if (token.includes('nba')) {
    return 'NBA';
  }

  if (token.includes('nfl')) {
    return 'NFL';
  }

  if (token.includes('mlb') || token.includes('baseball')) {
    return 'MLB';
  }

  if (token.includes('nhl') || token.includes('hockey')) {
    return 'NHL';
  }

  if (token.includes('mls') || token.includes('soccer')) {
    return 'MLS';
  }

  if (token.includes('ncaafb') || (token.includes('ncaa') && token.includes('football'))) {
    return 'NCAA FB';
  }

  if (token.includes('ncaabb') || (token.includes('ncaa') && token.includes('basketball'))) {
    return 'NCAA BB';
  }

  if (token.includes('concert')) {
    return 'Concerts';
  }

  if (token.includes('theater') || token.includes('theatre') || token.includes('broadway')) {
    return 'Theater';
  }

  return raw;
};

const normalizeChannelName = (value?: string | null) => {
  const raw = (value ?? '').trim();
  const token = normalizeToken(raw);

  if (!token) {
    return 'Unknown';
  }

  if (token.includes('stubhub')) {
    return 'StubHub';
  }

  if (token.includes('seatgeek')) {
    return 'SeatGeek';
  }

  if (token.includes('ticketmaster') || token.includes('tmaster')) {
    return 'Ticketmaster';
  }

  if (token.includes('vividseats') || token === 'vivid') {
    return 'Vivid Seats';
  }

  if (token.includes('tickpick')) {
    return 'TickPick';
  }

  if (token.includes('gametime')) {
    return 'Gametime';
  }

  return raw;
};

const normalizeDateLabel = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return `${parsed.getMonth() + 1}/${parsed.getDate()}`;
};

const toAmount = (value?: number | null) => Number(value ?? 0);
const isValidUuid = (value?: string | null) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value ?? '');

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_SHORT_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const monthNameToIndex = (name?: string | null) => {
  if (!name) {
    return -1;
  }

  const normalized = String(name).trim().toLowerCase();
  const directIndex = MONTH_NAMES.findIndex((month) => month.toLowerCase() === normalized);
  if (directIndex >= 0) {
    return directIndex;
  }

  const shortIndex = MONTH_SHORT_NAMES.findIndex((month) => month === normalized.slice(0, 3));
  if (shortIndex >= 0) {
    return shortIndex;
  }

  const numericMonth = Number(normalized);
  if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    return numericMonth - 1;
  }

  return -1;
};

const normalizeStatus = (ticket: TrendsTimelineTicket) => {
  const rawStatus = ticket.tp_mobile_display_status || ticket.tp_status || '';
  const token = normalizeToken(rawStatus);

  if (token === 'sold') return 'sold';
  if (token === 'list' || token === 'listed') return 'listed';
  if (token === 'verified' || token === 'verification') return 'verified';
  if (token === 'tobeverified') return 'toBeVerified';
  if (token === 'delist' || token === 'delisted' || token === 'delistrequested' || token === 'listingrequestexpired' || token === 'delistinprogress') return 'delisted';
  if (token === 'unsold' || token === 'return' || token === 'returned') return 'unsold';

  return token;
};

const isSoldTicketStatus = (status: string) => {
  const normalized = String(status || '').toLowerCase().trim();
  const excludeStatuses = ['listed', 'cancelled', 'expired', 'delisted', 'returned', 'return'];
  if (excludeStatuses.some((token) => normalized.includes(token))) {
    return false;
  }

  const includeStatuses = ['sold', 'settle', 'complete', 'paid', 'finished', 'confirmed'];
  return includeStatuses.some((token) => normalized.includes(token));
};

const isActiveListedTicketStatus = (status: string) => {
  const normalized = String(status || '').toLowerCase().trim();
  return (
    (normalized === 'listed' || normalized === 'list' || normalized.includes('active')) &&
    !normalized.includes('delist') &&
    !normalized.includes('return') &&
    !isSoldTicketStatus(normalized)
  );
};

const isReturnedOrDelistedStatus = (status: string) => {
  const normalized = String(status || '').toLowerCase().trim();
  return normalized.includes('delist') || normalized.includes('return');
};

const getTicketQuantity = (ticket: TrendsTimelineTicket) => {
  const groupedCount = Array.isArray(ticket.grouped_ticket_data) ? ticket.grouped_ticket_data.length : 0;
  if (groupedCount > 0) return groupedCount;
  return 1;
};

const buildDateSeries = (startDate: string, endDate: string) => {
  const output: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return output;
  }

  const cursor = new Date(start);
  while (cursor <= end) {
    output.push(normalizeDateLabel(cursor.toISOString()));
    cursor.setDate(cursor.getDate() + 1);
  }

  return output;
};

const endOfDayInclusive = (dateStr: string) => {
  const d = new Date(`${dateStr}T23:59:59.999Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDayUtc = (dateStr: string) => {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const useTrendsData = ({
  startDate,
  endDate,
  period = '30D',
  userId,
  userRole,
  userEmail = '',
  timelineActivityDateFilter = false,
}: UseTrendsDataProps) => {
  const hasValidUserContext = isValidUuid(userId) && Boolean(userRole);
  const isMarketingUser = false;
  const channelStartAt = `${startDate}T00:00:00.000Z`;
  const channelEndAt = `${endDate}T23:59:59.999Z`;
  const parsedStartDate = useMemo(() => new Date(startDate), [startDate]);
  const parsedEndDate = useMemo(() => new Date(endDate), [endDate]);

  const revenueQueryVars = useMemo(
    () => getDashboardAlignedRevenueVars(period, startDate, endDate),
    [period, startDate, endDate],
  );
  const { startYear, endYear, startMonth: startMonthName, endMonth: endMonthName } = revenueQueryVars;
  const endMonthTwoDigit = String((Number.isNaN(parsedEndDate.getTime()) ? new Date().getMonth() : parsedEndDate.getMonth()) + 1).padStart(2, '0');

  const {
    data: salesRepLookupData,
    loading: salesRepLookupLoading,
    refetch: refetchSalesRepLookup,
  } = useQuery(GET_SALES_REPS_BY_EMAIL, {
    variables: { email: userEmail?.trim() },
    fetchPolicy: 'cache-and-network',
    skip: !isMarketingUser || !userEmail,
  });
  const {
    data: salesRepsListData,
    loading: salesRepsListLoading,
    refetch: refetchSalesRepsList,
  } = useQuery(GET_SALES_REPS, {
    fetchPolicy: 'cache-and-network',
    skip: !isMarketingUser,
  });
  const scopedSalesRepId = useMemo(() => {
    const fromDirectLookup = salesRepLookupData?.sales_reps?.[0]?.sr_id || null;
    if (fromDirectLookup) return fromDirectLookup;

    const normalizedEmail = String(userEmail || '').trim().toLowerCase();
    if (!normalizedEmail) return null;
    const fromListLookup = (salesRepsListData?.sales_reps || []).find(
      (rep: any) => String(rep?.sr_email || '').trim().toLowerCase() === normalizedEmail
    );
    return fromListLookup?.sr_id || null;
  }, [salesRepLookupData, salesRepsListData, userEmail]);

  const {
    data: managedUsersByRepData,
    loading: managedUsersByRepLoading,
    refetch: refetchManagedUsersByRep,
  } = useQuery(GET_MANAGED_USERS_BY_SALES_REP, {
    variables: { salesRepId: scopedSalesRepId },
    fetchPolicy: 'cache-and-network',
    skip: !isMarketingUser || !scopedSalesRepId,
  });

  const {
    data: managedUsersByUserIdData,
    loading: managedUsersByUserIdLoading,
    refetch: refetchManagedUsersByUserId,
  } = useQuery(GET_MANAGED_USERS_BY_SALES_REP, {
    variables: { salesRepId: userId },
    fetchPolicy: 'cache-and-network',
    // Some environments store `u_sales_reps_id` as the auth user id instead of `sales_reps.sr_id`.
    skip: !isMarketingUser || !isValidUuid(userId) || scopedSalesRepId === userId,
  });

  const {
    data: usersListFallbackData,
    loading: usersListFallbackLoading,
    refetch: refetchUsersListFallback,
  } = useQuery(GET_USERS_LIST, {
    variables: {
      pageSize: 5000,
      pageOffset: 0,
      searchText: '%',
      order_by: [{ u_created_at: 'desc' }],
    },
    fetchPolicy: 'cache-and-network',
    skip: !isMarketingUser,
  });

  const managedUserIds = useMemo(
    () => {
      const primary = (managedUsersByRepData?.managedUsers || []).map((user: any) => user?.u_id).filter(Boolean);
      const fallback = (managedUsersByUserIdData?.managedUsers || []).map((user: any) => user?.u_id).filter(Boolean);
      const usersList = (usersListFallbackData?.user || [])
        .filter((user: any) => {
          const repId = user?.u_sales_reps_id;
          if (!repId) return false;
          const matchesRep =
            (scopedSalesRepId && repId === scopedSalesRepId) ||
            (isValidUuid(userId) && repId === userId);
          if (!matchesRep) return false;
          return user?.u_delete_requested !== true;
        })
        .map((user: any) => user?.u_id)
        .filter(Boolean);
      const chosen = primary.length > 0 ? primary : (fallback.length > 0 ? fallback : usersList);
      return new Set(chosen);
    },
    [managedUsersByRepData, managedUsersByUserIdData, usersListFallbackData, scopedSalesRepId, userId]
  );

  const {
    data: timelineResponse,
    loading: timelineLoading,
    error: timelineError,
    refetch: refetchTimeline,
  } = useQuery(GET_TRENDS_TICKET_TIMELINE, {
    variables: {
      // Server function filters by event date; omit range when we filter by latest_update client-side.
      startdate: timelineActivityDateFilter ? null : startDate,
      enddate: timelineActivityDateFilter ? null : endDate,
      pageSize: timelineActivityDateFilter ? 3000 : 1000,
      pageOffset: 0,
    },
    fetchPolicy: 'cache-and-network',
    skip: false,
  });

  const {
    data: channelResponse,
    loading: channelLoading,
    error: channelError,
    refetch: refetchChannels,
  } = useQuery(GET_TRENDS_SALES_CHANNELS, {
    variables: {
      startdate: channelStartAt,
      enddate: channelEndAt,
      pageSize: 5000,
      pageOffset: 0,
    },
    fetchPolicy: 'cache-and-network',
    skip: false,
  });

  const {
    data: pendingResponse,
    loading: pendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = useQuery(GET_TRENDS_PENDING_DATA, {
    variables: {
      userId,
      userRole
    },
    fetchPolicy: 'cache-and-network',
    skip: !hasValidUserContext || isMarketingUser,
  });

  const {
    data: revenueResponse,
    loading: revenueLoading,
    error: revenueError,
    refetch: refetchRevenue,
  } = useQuery(GET_REVENUE_ADMIN, {
    variables: {
      startYear,
      endYear,
      startMonth: startMonthName,
      endMonth: endMonthName,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: isMarketingUser,
  });

  const {
    data: ticketStatusCountsResponse,
    refetch: refetchTicketStatusCounts,
  } = useQuery(GET_TICKET_STATUS_COUNTS, {
    fetchPolicy: 'cache-and-network',
    skip: isMarketingUser,
  });

  const allTimeSoldCount = ticketStatusCountsResponse?.soldCount?.aggregate?.count ?? null;

  const {
    data: scopedRevenueResponse,
    loading: scopedRevenueLoading,
    error: scopedRevenueError,
    refetch: refetchScopedRevenue,
  } = useQuery(GET_TRENDS_SUPER_USER_DATA, {
    variables: {
      startYear,
      endYear,
      startMonth: startMonthName,
      endMonth: endMonthName,
      userId,
    },
    fetchPolicy: 'cache-and-network',
    // This query is blocked by Hasura allowlists in the current environments.
    // Marketing-user revenue is derived from the ticket timeline below.
    skip: true,
  });

  const {
    data: topSalesResponse,
    loading: topSalesLoading,
    error: topSalesError,
    refetch: refetchTopSales,
  } = useQuery(GET_TRENDS_TOP_SALES, {
    variables: {
      month: endMonthName,
      year: String(endYear),
      userId,
      userRole
    },
    fetchPolicy: 'cache-and-network',
    skip: !hasValidUserContext || isMarketingUser,
  });

  const ticketTimeline = useMemo<TrendsTimelineTicket[]>(() => {
    const raw = timelineResponse?.filterlistticketswithvlids ?? [];
    const hasManagedScope = managedUserIds.size > 0;
    const scopedRaw = isMarketingUser
      ? (hasManagedScope
          ? raw.filter((ticket: TrendsTimelineTicket) => managedUserIds.has(ticket.user_id))
          : [])
      : raw;
    if (!timelineActivityDateFilter) {
      return scopedRaw;
    }
    const from = startOfDayUtc(startDate);
    const to = endOfDayInclusive(endDate);
    if (!from || !to) {
      return scopedRaw;
    }
    return scopedRaw.filter((ticket: TrendsTimelineTicket) => {
      const lu = new Date(ticket.latest_update);
      if (Number.isNaN(lu.getTime())) {
        return false;
      }
      return lu >= from && lu <= to;
    });
  }, [timelineActivityDateFilter, timelineResponse, startDate, endDate, isMarketingUser, managedUserIds]);

  const channelTickets = useMemo<TrendsChannelTicket[]>(() => {
    // Channel query payload has no user_id, so hide it for marketing users to avoid cross-user leakage.
    if (isMarketingUser) return [];
    return channelResponse?.getmanageticketwithvlids ?? [];
  }, [channelResponse, isMarketingUser]);

  const derivedRevenueByMonthForMarketing = useMemo<TrendsRevenuePoint[]>(() => {
    if (!isMarketingUser) return [];

    const totals = new Map<string, { revenue: number; totalTickets: number; sortKey: number }>();
    ticketTimeline.forEach((ticket) => {
      const ts = new Date(ticket.latest_update);
      if (Number.isNaN(ts.getTime())) return;
      const monthName = MONTH_NAMES[ts.getUTCMonth()];
      const year = ts.getUTCFullYear();
      const key = `${year}-${String(ts.getUTCMonth() + 1).padStart(2, '0')}`;
      const row = totals.get(key) ?? { revenue: 0, totalTickets: 0, sortKey: year * 100 + ts.getUTCMonth() + 1 };
      row.revenue += toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price);
      row.totalTickets += getTicketQuantity(ticket);
      totals.set(key, row);
      if (!monthName) return;
    });

    return Array.from(totals.entries())
      .sort((a, b) => a[1].sortKey - b[1].sortKey)
      .map(([key, value]) => {
        const monthIndex = Number(key.split('-')[1]) - 1;
        return {
          monthsName: MONTH_NAMES[monthIndex] || 'Unknown',
          revenue: Number(value.revenue.toFixed(2)),
          totalTickets: value.totalTickets,
        };
      });
  }, [isMarketingUser, ticketTimeline]);

  const revenueByMonth = useMemo<TrendsRevenuePoint[]>(() => {
    if (isMarketingUser) {
      return derivedRevenueByMonthForMarketing;
    }
    const points = revenueResponse?.getRevenueForAdmin;
    return Array.isArray(points) ? points : [];
  }, [revenueResponse, isMarketingUser, derivedRevenueByMonthForMarketing]);

  const revenueAggregate = useMemo<TrendsRevenueAggregate | null>(() => {
    if (isMarketingUser) {
      const overAllSales = ticketTimeline.reduce(
        (sum, ticket) => sum + (toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price)),
        0
      );
      const overAllTickets = ticketTimeline.reduce((sum, ticket) => sum + getTicketQuantity(ticket), 0);
      return {
        overAllTickets,
        overAllSales,
        avgListPrice: overAllTickets > 0 ? Number((overAllSales / overAllTickets).toFixed(2)) : 0,
        quickasystFee: Number((overAllSales * 0.08).toFixed(2)),
      };
    }

    if (revenueLoading) {
      return null;
    }

    const aggregate = revenueResponse?.getRevenueForAdminAggregate;
    if (!aggregate) return null;
    if (Array.isArray(aggregate)) {
      return aggregate.length > 0 ? (aggregate[0] as TrendsRevenueAggregate) : null;
    }

    return aggregate as TrendsRevenueAggregate;
  }, [revenueResponse, revenueLoading, isMarketingUser, ticketTimeline]);

  const topSalesByLeague = useMemo<TrendsTopSalesPoint[]>(() => {
    if (isMarketingUser) {
      const totals = new Map<string, number>();
      ticketTimeline.forEach((ticket) => {
        const key = normalizeLeagueName(ticket.league_name);
        totals.set(key, (totals.get(key) || 0) + getTicketQuantity(ticket));
      });
      return Array.from(totals.entries()).map(([leagueName, totalCounts]) => ({ leagueName, totalCounts }));
    }
    return topSalesResponse?.getTopSales ?? [];
  }, [topSalesResponse, isMarketingUser, ticketTimeline]);

  const chartData = useMemo<TrendsChartDataPoint[]>(() => {
    const pendingStatsRaw = pendingResponse?.getPendingActiveTicketsCountAndPayout;
    const pendingStats = Array.isArray(pendingStatsRaw) ? pendingStatsRaw[0] : pendingStatsRaw;
    const pendingCount = isMarketingUser
      ? ticketTimeline.filter((ticket) => {
          const status = normalizeStatus(ticket);
          return status === 'toBeVerified' || status === 'verified';
        }).length
      : Number(pendingStats?.totalCount ?? 0);

    // Compute fee ratio from real DB aggregate; fall back to 8% only when unavailable
    const feeRatio =
      revenueAggregate && revenueAggregate.overAllSales > 0
        ? revenueAggregate.quickasystFee / revenueAggregate.overAllSales
        : 0.08;

    if (ticketTimeline.length === 0 && revenueByMonth.length > 0) {
      return revenueByMonth
        .filter((point) => monthNameToIndex(point.monthsName) >= 0)
        .sort((a, b) => monthNameToIndex(a.monthsName) - monthNameToIndex(b.monthsName))
        .map((point) => {
          const monthIndex = monthNameToIndex(point.monthsName);
          const revenue = Number(point.revenue ?? 0);
          const sold = Number(point.totalTickets ?? 0);
          const qaProfit = Math.round(revenue * feeRatio);
          const vlFee = Math.round(revenue * 0.08);
          const userPayout = Math.max(0, revenue - qaProfit - vlFee);
          const avgListPrice = sold > 0 ? Math.round(revenue / sold) : Math.round(Number(revenueAggregate?.avgListPrice ?? 0));

          return {
            date: `${monthIndex + 1}/1`,
            revenue,
            profit: qaProfit,
            sold,
            price: avgListPrice,
            qaProfit,
            userPayout,
            vlFee,
            pending: pendingCount,
            listed: sold,
            verified: 0,
            toBeVerified: 0,
            delisted: 0,
            unsold: 0,
          };
        });
    }

    const labels = buildDateSeries(startDate, endDate);
    const byDate = new Map<string, TrendsChartDataPoint>();

    labels.forEach((label) => {
      byDate.set(label, {
        date: label,
        revenue: 0,
        profit: 0,
        sold: 0,
        price: 0,
        qaProfit: 0,
        userPayout: 0,
        vlFee: 0,
        pending: 0,
        listed: 0,
        verified: 0,
        toBeVerified: 0,
        delisted: 0,
        unsold: 0,
      });
    });

    const averageSource = new Map<string, { totalPrice: number; count: number }>();

    ticketTimeline.forEach((ticket) => {
      const label = normalizeDateLabel(ticket.latest_update);
      const base = byDate.get(label);
      if (!base) {
        return;
      }

      const quantity = getTicketQuantity(ticket);
      const soldAmount = toAmount(ticket.tp_sold_amount);
      const listAmount = toAmount(ticket.tp_list_price);
      const status = normalizeStatus(ticket);

      if (status === 'sold') {
        base.revenue += soldAmount || listAmount;
        base.sold += quantity;
        base.profit += Math.round((soldAmount || listAmount) * feeRatio);
      }

      if (status === 'listed') {
        base.listed += quantity;
      }

      if (status === 'verified') {
        base.verified += quantity;
      }

      if (status === 'toBeVerified') {
        base.toBeVerified += quantity;
      }

      if (status === 'delisted') {
        base.delisted += quantity;
      }

      if (status === 'unsold') {
        base.unsold += quantity;
      }

      const priceBucket = averageSource.get(label) ?? { totalPrice: 0, count: 0 };
      priceBucket.totalPrice += (soldAmount || listAmount) * quantity;
      priceBucket.count += quantity;
      averageSource.set(label, priceBucket);
    });

    return Array.from(byDate.values()).map((point) => {
      const average = averageSource.get(point.date);
      const avgPrice = average && average.count > 0 ? Math.round(average.totalPrice / average.count) : 0;
      const qaProfit = Math.round(point.revenue * feeRatio);
      const vlFee = Math.round(point.revenue * 0.08);
      const userPayout = Math.max(0, point.revenue - qaProfit - vlFee);

      return {
        ...point,
        price: avgPrice,
        qaProfit,
        vlFee,
        userPayout,
        pending: point.pending || pendingCount,
      };
    });
  }, [endDate, pendingResponse, revenueAggregate, revenueByMonth, startDate, ticketTimeline, isMarketingUser]);

  const leagueData = useMemo<LeagueSalesData[]>(() => {
    if (ticketTimeline.length === 0 && topSalesByLeague.length > 0) {
      const avgPrice = Number(revenueAggregate?.avgListPrice ?? 0);
      return topSalesByLeague
        .map((item) => {
          const name = normalizeLeagueName(item.leagueName);
          const tickets = Number(item.totalCounts ?? 0);
          return {
            name,
            tickets,
            revenue: Math.round(tickets * avgPrice),
            color: LEAGUE_COLORS[name] || '#64748b',
          } as LeagueSalesData;
        })
        .sort((left, right) => right.revenue - left.revenue);
    }

    const totals = new Map<string, LeagueSalesData>();

    ticketTimeline.forEach((ticket) => {
      const leagueName = normalizeLeagueName(ticket.league_name);
      const existing = totals.get(leagueName) ?? {
        name: leagueName,
        revenue: 0,
        tickets: 0,
        color: LEAGUE_COLORS[leagueName] || '#64748b',
      };

      const quantity = getTicketQuantity(ticket);
      existing.revenue += toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price);
      existing.tickets += quantity;
      totals.set(leagueName, existing);
    });

    return Array.from(totals.values()).sort((left, right) => right.revenue - left.revenue);
  }, [ticketTimeline, topSalesByLeague, revenueAggregate]);

// Marketplace Performance Data - DIRECT FROM SOLD CHANNEL TICKETS ONLY
const marketplaceData = useMemo(() => {
  const qaFeeRatio = 0.08;
  const totals = new Map<string, { tickets: number; revenue: number }>();
  
  console.log('[MARKETPLACE_DATA_CALC]', {
    channelTicketsCount: channelTickets.length,
    startDate,
    endDate,
  });
  
  if (!channelTickets || channelTickets.length === 0) {
    console.log('[MARKETPLACE_DATA_CALC] No channel tickets available');
    return [];
  }
  
  // ONLY use channelTickets - this is the correct source for marketplace data
  // These should already be filtered to sold tickets by the backend query
  channelTickets.forEach((item: any, index: number) => {
    // Normalize and validate marketplace/channel name
    let marketplace = (item?.channel || '').toString().trim();
    
    // Fallback to other field names if channel is empty
    if (!marketplace) {
      marketplace = (item?.marketplace || '').toString().trim();
    }
    if (!marketplace) {
      marketplace = (item?.platform || '').toString().trim();
    }
    if (!marketplace) {
      marketplace = (item?.source || '').toString().trim();
    }
    
    // Skip if no valid marketplace name
    if (!marketplace || marketplace === 'null' || marketplace === 'undefined') {
      console.log(`[MARKETPLACE_DATA_CALC] Skipping ticket ${index} - no marketplace:`, {
        id: item?.id,
        channel: item?.channel,
        status: item?.status,
      });
      return;
    }
    
    // Get the sold price (should be tp_sold_amount from backend)
    const price = Number(item?.price ?? 0);
    if (price <= 0) {
      console.log(`[MARKETPLACE_DATA_CALC] Skipping ticket ${index} - invalid price:`, {
        id: item?.id,
        marketplace,
        price: item?.price,
        status: item?.status,
      });
      return;
    }
    
    // Verify status indicates sold (should already be filtered, but double-check)
    const status = (item?.status || '').toString().toLowerCase();
    if (!status.includes('sold') && !status.includes('settled')) {
      console.log(`[MARKETPLACE_DATA_CALC] Skipping ticket ${index} - not sold:`, {
        id: item?.id,
        marketplace,
        status: item?.status,
      });
      return;
    }
    
    // Normalize marketplace name
    const normalized = normalizeChannelName(marketplace);
    
    // Aggregate
    const existing = totals.get(normalized) || { tickets: 0, revenue: 0 };
    existing.tickets += 1;
    existing.revenue += price;
    totals.set(normalized, existing);
    
    console.log(`[MARKETPLACE_DATA_CALC] Added ticket: ${normalized} | $${price} | Status: ${status}`);
  });
  
  const finalData = Array.from(totals.entries());
  console.log('[MARKETPLACE_DATA_CALC] Final aggregation:', {
    totalMarketplaces: finalData.length,
    totalTickets: Array.from(totals.values()).reduce((sum, d) => sum + d.tickets, 0),
    totalRevenue: Array.from(totals.values()).reduce((sum, d) => sum + d.revenue, 0),
    marketplaces: finalData.map(([name, data]) => ({
      name,
      tickets: data.tickets,
      revenue: data.revenue,
    })),
  });
  
  // Colors for known marketplaces
  const colors: Record<string, string> = {
    'StubHub': '#f97316',
    'Ticketmaster': '#3b82f6',
    'SeatGeek': '#f59e0b',
    'Vivid Seats': '#8b5cf6',
    'Gametime': '#ec4899',
    'TickPick': '#10b981',
  };
  
  return finalData
    .map(([name, data]) => ({
      name,
      tickets: data.tickets,
      revenue: data.revenue,
      paid: Math.round(data.revenue * (1 - qaFeeRatio) * 100) / 100,
      profit: Math.round(data.revenue * qaFeeRatio * 100) / 100,
      color: colors[name] || '#94a3b8',
    }))
    .sort((a, b) => b.revenue - a.revenue);
}, [channelTickets, startDate, endDate]);

  const leagueSeries = useMemo<Record<string, Record<string, number>>>(() => {
    if (ticketTimeline.length === 0 && topSalesByLeague.length > 0) {
      const output: Record<string, Record<string, number>> = {};
      const avgPrice = Number(revenueAggregate?.avgListPrice ?? 0);
      const lastDateLabel = normalizeDateLabel(new Date(endDate).toISOString());

      topSalesByLeague.forEach((item) => {
        const leagueName = normalizeLeagueName(item.leagueName);
        const estimatedRevenue = Math.round(Number(item.totalCounts ?? 0) * avgPrice);

        if (!output[leagueName]) {
          output[leagueName] = {};
        }

        output[leagueName][lastDateLabel] = estimatedRevenue;
      });

      return output;
    }

    const output: Record<string, Record<string, number>> = {};

    ticketTimeline.forEach((ticket) => {
      const label = normalizeDateLabel(ticket.latest_update);
      const leagueName = normalizeLeagueName(ticket.league_name);
      const amount = toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price);

      if (!output[leagueName]) {
        output[leagueName] = {};
      }

      output[leagueName][label] = (output[leagueName][label] ?? 0) + amount;
    });

    return output;
  }, [ticketTimeline, topSalesByLeague, revenueAggregate, endDate]);

  const marketplaceSeries = useMemo<Record<string, Record<string, number>>>(() => {
    const output: Record<string, Record<string, number>> = {};

    channelTickets.forEach((ticket) => {
      const label = normalizeDateLabel(ticket.created_at);
      const channel = normalizeChannelName(ticket.channel);
      const amount = toAmount(ticket.price);

      if (!output[channel]) {
        output[channel] = {};
      }

      output[channel][label] = (output[channel][label] ?? 0) + amount;
    });

    return output;
  }, [channelTickets]);

  const organizationStats = useMemo(() => {
    const users = new Set(ticketTimeline.map((ticket) => ticket.user_id).filter(Boolean));
    const activeUsers = new Set(
      ticketTimeline
        .filter((ticket) => (toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price)) > 0)
        .map((ticket) => ticket.user_id)
        .filter(Boolean)
    );

    const totalRevenue = ticketTimeline.reduce(
      (sum, ticket) => sum + (toAmount(ticket.tp_sold_amount) || toAmount(ticket.tp_list_price)),
      0
    );

    const totalTicketCount = ticketTimeline.reduce((sum, ticket) => sum + getTicketQuantity(ticket), 0);
    const delistedCount = ticketTimeline.reduce((sum, ticket) => {
      const status = String(ticket.tp_mobile_display_status || ticket.tp_status || '').toLowerCase();
      return sum + (isReturnedOrDelistedStatus(status) ? getTicketQuantity(ticket) : 0);
    }, 0);
    const otherTicketCount = Math.max(0, totalTicketCount - delistedCount);

    return {
      totalUsers: users.size,
      activeUsers: activeUsers.size,
      avgRevenuePerUser: users.size > 0 ? Math.round(totalRevenue / users.size) : 0,
      delistRate: otherTicketCount > 0 ? Number(((delistedCount / otherTicketCount) * 100).toFixed(1)) : 0,
    };
  }, [ticketTimeline]);

  useEffect(() => {
    // Keep explicit debug logs for validating API -> UI data flow with DB checks.
    console.log('[ANALYTICS][REQUEST]', {
      startDate,
      endDate,
      userId,
      userRole,
      startYear,
      endYear,
      startMonthName,
      endMonthName,
      endMonthTwoDigit,
    });
  }, [
    startDate,
    endDate,
    userId,
    userRole,
    startYear,
    endYear,
    startMonthName,
    endMonthName,
    endMonthTwoDigit,
  ]);

  useEffect(() => {
    console.log('[ANALYTICS][RAW_RESPONSE_COUNTS]', {
      timelineCount: ticketTimeline.length,
      channelCount: channelTickets.length,
      revenuePoints: revenueByMonth.length,
      topSalesPoints: topSalesByLeague.length,
      pendingTotal: Number(
        (Array.isArray(pendingResponse?.getPendingActiveTicketsCountAndPayout)
          ? pendingResponse?.getPendingActiveTicketsCountAndPayout?.[0]
          : pendingResponse?.getPendingActiveTicketsCountAndPayout)?.totalCount ?? 0
      ),
      pendingPayout: Number(
        (Array.isArray(pendingResponse?.getPendingActiveTicketsCountAndPayout)
          ? pendingResponse?.getPendingActiveTicketsCountAndPayout?.[0]
          : pendingResponse?.getPendingActiveTicketsCountAndPayout)?.potentialPayout ?? 0
      ),
    });
  }, [ticketTimeline, channelTickets, revenueByMonth, topSalesByLeague, pendingResponse]);

  useEffect(() => {
    console.log('[ANALYTICS][RAW_RESPONSE_SAMPLE]', {
      timelineFirst: ticketTimeline[0] ?? null,
      channelFirst: channelTickets[0] ?? null,
      revenueFirst: revenueByMonth[0] ?? null,
      topSalesFirst: topSalesByLeague[0] ?? null,
      pending: Array.isArray(pendingResponse?.getPendingActiveTicketsCountAndPayout)
        ? pendingResponse?.getPendingActiveTicketsCountAndPayout?.[0] ?? null
        : pendingResponse?.getPendingActiveTicketsCountAndPayout ?? null,
    });
  }, [ticketTimeline, channelTickets, revenueByMonth, topSalesByLeague, pendingResponse]);

  useEffect(() => {
    const totals = chartData.reduce(
      (acc, row) => {
        acc.revenue += Number(row.revenue ?? 0);
        acc.qaProfit += Number(row.qaProfit ?? 0);
        acc.sold += Number(row.sold ?? 0);
        return acc;
      },
      { revenue: 0, qaProfit: 0, sold: 0 }
    );

    console.log('[ANALYTICS][DERIVED_CHART_TOTALS]', {
      points: chartData.length,
      totalRevenue: totals.revenue,
      totalQaProfit: totals.qaProfit,
      totalSold: totals.sold,
      organizationStats,
    });
  }, [chartData, organizationStats]);

  const refetch = () => Promise.all([
    refetchTimeline(),
    refetchChannels(),
    ...(isMarketingUser ? [] : [refetchPending()]),
    ...(isMarketingUser ? [] : [refetchRevenue()]),
    ...(isMarketingUser ? [] : [refetchTicketStatusCounts()]),
    ...(isMarketingUser ? [] : [refetchTopSales()]),
    ...(isMarketingUser
      ? [refetchSalesRepLookup(), refetchSalesRepsList(), refetchManagedUsersByRep(), refetchManagedUsersByUserId(), refetchUsersListFallback()]
      : []),
  ]);

  return {
    chartData,
    leagueData,
    marketplaceData,
    channelTickets,
    leagueSeries,
    marketplaceSeries,
    organizationStats,
    revenueAggregate,
    revenueByMonth,
    revenueLoading,
    allTimeSoldCount,
    ticketTimeline,
    loading: timelineLoading || channelLoading || pendingLoading || revenueLoading || scopedRevenueLoading || topSalesLoading || salesRepLookupLoading || salesRepsListLoading || managedUsersByRepLoading || managedUsersByUserIdLoading || usersListFallbackLoading,
    error: timelineError || channelError || pendingError || revenueError || scopedRevenueError || topSalesError,
    hasLiveData: ticketTimeline.length > 0 || channelTickets.length > 0,
    refetch,
  };
};

export const getDateRangeForPeriod = (period: string): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date(end);

  switch (period) {
    case '7D':
      start.setDate(end.getDate() - 6);
      break;
    case '30D':
      start.setDate(end.getDate() - 29);
      break;
    case '90D':
      start.setDate(end.getDate() - 89);
      break;
    case 'YTD':
      start.setMonth(0, 1);
      break;
    case 'All':
      start.setFullYear(end.getFullYear() - 1);
      start.setMonth(0, 1);
      break;
    default:
      start.setDate(end.getDate() - 29);
      break;
  }

  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
};